import React from "react";

import API from "@beardeddevops/react.api";
import Types from "constants/Types";
import Framework from "@beardeddevops/react.framework";
import {Objects} from "components";
import UserRoles from "constants/UserRoles";
import {Button} from "reactstrap";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import ReactTable from "react-table";
import {Link} from "react-router-dom";
import {confirmAlert} from "react-confirm-alert";
import {Tooltip} from "@material-ui/core";
import {avCenterAuditLabel} from "utilities/avCenterAudit";

export default class CustomerAvCenter extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			loading: true,
			error: null,
			summary: null,
			modalStripeAdd: false,
			actionBusy: null,
			copiedKey: null,
		};
	}

	componentDidMount() {
		this.loadSummary();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.parent !== this.props.parent || prevProps.reloadSelf !== this.props.reloadSelf) {
			this.loadSummary();
		}
	}

	canManage = () => {
		const roles = this.props.roles || [];
		return roles.some((role) => [UserRoles.Admin, UserRoles.Manager, UserRoles.Executive].includes(role));
	};

	loadSummary = async () => {
		if (!this.props.parent) {
			return;
		}
		this.setState({loading: true, error: null});
		try {
			const model = new API.BuildClass(Types.Av_Center._name, API.RequestType.READ);
			model.parent = this.props.parent;
			const response = await model.submit(null, 'GET_CUSTOMER');
			this.setState({summary: response, loading: false});
		} catch (err) {
			this.setState({
				error: err?.message || 'Could not load AV Center data',
				loading: false,
			});
		}
	};

	runAction = async (key, action) => {
		this.setState({actionBusy: key, error: null});
		try {
			await action();
			await this.loadSummary();
			if (this.props.reloadPage) {
				this.props.reloadPage();
			}
		} catch (err) {
			this.setState({error: err?.message || 'Action failed'});
		} finally {
			this.setState({actionBusy: null});
		}
	};

	cancelSubscription = (row) => {
		confirmAlert({
			title: 'Cancel subscription',
			message: 'Schedule cancellation at the end of the current billing period?',
			buttons: [
				{
					label: 'Yes, cancel at period end',
					onClick: () => this.runAction(`cancel-${row.av_key_pkey}`, async () => {
						const model = new API.BuildClass(Types.Stripe_Charges._name, API.RequestType.UPDATE);
						model.object.customer = this.props.parent;
						model.object.stripe_subscription_id = row.stripe_subscription_id;
						model.object.stripe_price_id = row.stripe_price_id;
						await model.submit(null, 'POST_SUBSCRIPTION_CANCEL_STAFF');
					}),
				},
				{label: 'No', onClick: () => {}},
			],
		});
	};

	sendPortalLink = (row) => this.runAction(`portal-${row.av_key_pkey}`, async () => {
		const model = new API.BuildClass(Types.Stripe_Charges._name, API.RequestType.UPDATE);
		model.object.customer = this.props.parent;
		model.object.stripe_subscription_id = row.stripe_subscription_id;
		model.object.stripe_price_id = row.stripe_price_id;
		await model.submit(null, 'POST_SEND_CUSTOMER_PORTAL_LINK');
	});

	repushKey = (row) => this.runAction(`repush-${row.av_key_pkey}`, async () => {
		const model = new API.BuildClass(Types.Av_Keys._name, API.RequestType.UPDATE);
		model.object.pkey = row.av_key_pkey;
		await model.submit(null, 'POST_REPUSH');
	});

	regenerateLink = (row) => this.runAction(`regen-${row.av_key_pkey}`, async () => {
		const avModel = new API.BuildClass(Types.Av_Links._name, API.RequestType.UPDATE);
		avModel.object.customer = this.props.parent;
		avModel.object.av_pkey = row.av_key_pkey;
		await avModel.submit(null, 'POST_REGENERATE_LINK');
	});

	titleCase = (value) => {
		if (value == null || value === '') {
			return '';
		}
		return String(value)
			.replace(/[_-]+/g, ' ')
			.replace(/\b\w/g, (letter) => letter.toUpperCase());
	};

	copyKey = (key) => {
		if (!key) {
			return;
		}
		const markCopied = () => {
			this.setState({copiedKey: key}, () => {
				setTimeout(() => {
					this.setState((s) => (s.copiedKey === key ? {copiedKey: null} : s));
				}, 1500);
			});
		};
		if (navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(key).then(markCopied).catch(() => {});
			return;
		}
		const ta = document.createElement('textarea');
		ta.value = key;
		ta.style.position = 'fixed';
		ta.style.opacity = '0';
		document.body.appendChild(ta);
		ta.select();
		let ok = false;
		try {
			ok = document.execCommand('copy');
		} catch (e) {
			ok = false;
		}
		document.body.removeChild(ta);
		if (ok) {
			markCopied();
		}
	};

	renderDeepLinks = (deepLinks = {}) => {
		const labels = {
			upcoming_renewal: 'Upcoming Renewal',
			inactive_subscription: 'Renewal Removal',
			unmatched_stripe: 'Unmatched Stripe',
			renewal_email_log: 'Renewal Email',
		};
		return Object.entries(deepLinks).map(([key, url]) => {
			const match = String(url).match(/^https?:\/\/[^/]+(\/[^#?]*)([#?].*)?$/);
			const pathname = match ? match[1] : url;
			const hash = match && match[2] && match[2].startsWith('#') ? match[2] : '';
			return (
				<div key={key}>
					<Link to={{pathname, hash}}>{labels[key] || key}</Link>
				</div>
			);
		});
	};

	render() {
		const {summary, loading, error, actionBusy} = this.state;
		const canManage = this.canManage();

		if (loading && !summary) {
			return <Framework.Elements.LoadingSpinner isLoading={true}/>;
		}

		const subscriptions = summary?.subscriptions || [];
		const billing = summary?.billing || [];
		const paymentMethod = summary?.payment_method;
		const activity = summary?.activity || [];
		const hasManagerLinks = subscriptions.some((row) => row.deep_links && Object.keys(row.deep_links).length > 0);

		const subscriptionColumns = [
			{Header: 'Product', accessor: 'product_name', id: 'product_name'},
			{
				Header: 'Sold',
				id: 'sold_date',
				accessor: 'sold_date',
				Cell: (props) => {
					const row = props.original;
					if (!row.sold_date) {
						return '-';
					}
					if (row.ticket_pkey) {
						return <Link to={`/tickets/${row.ticket_pkey}`}>{row.sold_date}</Link>;
					}
					return row.sold_date;
				},
			},
			{Header: 'Renewal / Period End', accessor: 'period_end', id: 'period_end'},
			{
				Header: 'Status',
				id: 'stripe_status',
				accessor: (row) => this.titleCase(row.stripe_status) || '-',
			},
			{
				Header: 'Cancel At Period End',
				id: 'cancel_at_period_end',
				accessor: (row) => row.cancel_at_period_end ? 'Yes' : 'No',
			},
			{
			Header: 'Key',
			accessor: 'activation_key',
			id: 'activation_key',
			width: 220,
			Cell: (props) => {
				const key = props.original.activation_key || '';
				if (!key) {
					return '-';
				}
				const copied = this.state.copiedKey === key;
				return (
					<div style={{whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word'}}>
						<span title={key} style={{wordBreak: 'break-all', fontFamily: 'monospace'}}>{key}</span>{' '}
						<Button
							size="sm"
							color="link"
							style={{padding: 0, fontSize: '0.85rem'}}
							onClick={() => this.copyKey(key)}
						>
							{copied ? 'Copied' : 'Copy'}
						</Button>
					</div>
				);
			},
		},
		];
		if (hasManagerLinks) {
			subscriptionColumns.push({
				Header: 'Manager Links',
				id: 'deep_links',
				sortable: false,
				filterable: false,
				Cell: (props) => this.renderDeepLinks(props.original.deep_links),
			});
		}

		if (canManage) {
			subscriptionColumns.push({
				Header: 'Actions',
				id: 'actions',
				sortable: false,
				filterable: false,
				width: 460,
				Cell: (props) => {
					const row = props.original;
					const busy = actionBusy != null && (
						actionBusy === `cancel-${row.av_key_pkey}`
						|| actionBusy === `portal-${row.av_key_pkey}`
						|| actionBusy === `repush-${row.av_key_pkey}`
						|| actionBusy === `regen-${row.av_key_pkey}`
					);
					return (
						<div className="d-flex flex-wrap" style={{gap: 4}}>
							{row.can_send_portal ? (
								<Button size="sm" color="primary" disabled={busy} onClick={() => this.sendPortalLink(row)}>
									Send Portal Link
								</Button>
							) : null}
							{row.can_cancel ? (
								<Button size="sm" color="warning" disabled={busy} onClick={() => this.cancelSubscription(row)}>
									Cancel Sub
								</Button>
							) : null}
							{row.can_repush ? (
								<Tooltip title="Email the current activation key or BitDefender download link. This does not charge the customer.">
									<span>
										<Button size="sm" color="info" disabled={busy} onClick={() => this.repushKey(row)}>
											Repush Key
										</Button>
									</span>
								</Tooltip>
							) : null}
							{row.product_type === 'bitdefender' ? (
								<Tooltip title="Refresh BitDefender download link (1 hour)">
									<span>
										<Button size="sm" color="secondary" disabled={busy} onClick={() => this.regenerateLink(row)}>
											Regen Link
										</Button>
									</span>
								</Tooltip>
							) : null}
						</div>
					);
				},
			});
		}

		const billingColumns = [
			{Header: 'Date', accessor: 'date', id: 'date'},
			{Header: 'Product', accessor: 'product_name', id: 'product_name'},
			{Header: 'Source', accessor: 'source', id: 'source'},
			{Header: 'Amount', accessor: 'amount', id: 'amount'},
			{
				Header: 'Status',
				id: 'status',
				accessor: (row) => this.titleCase(row.status) || '-',
			},
			{
				Header: 'Invoice',
				id: 'invoice',
				Cell: (props) => {
					const row = props.original;
					if (row.hosted_url) {
						return <a href={row.hosted_url} target="_blank" rel="noopener noreferrer">View Invoice</a>;
					}
					return '-';
				},
			},
		];

		const activityColumns = [
			{Header: 'When', accessor: 'created', id: 'created'},
			{Header: 'Who', accessor: 'actor_name', id: 'actor_name'},
			{
				Header: 'Action',
				id: 'action',
				accessor: (row) => avCenterAuditLabel(row.action),
			},
			{
				Header: 'Result',
				id: 'outcome',
				accessor: (row) => this.titleCase(row.outcome) || '-',
			},
			{Header: 'Detail', accessor: 'detail', id: 'detail'},
		];

		return (
			<div>
				{error ? <div className="alert alert-danger" role="alert">{error}</div> : null}

				<div className="d-flex justify-content-between align-items-center w-100 mb-3">
					<h5 className="mb-0">AV Center</h5>
					{canManage ? (
						<Button size="sm" color="primary" className="ml-auto" onClick={() => this.setState({modalStripeAdd: true})}>
							Purchase More AV
						</Button>
					) : null}
				</div>

				<Card className="mb-3">
					<CardContent>
						<h6>Customer Billing</h6>
						<div className="d-flex flex-wrap" style={{gap: '1.5rem'}}>
							<div>
								<div className="text-muted">Stripe Customer</div>
								<div>{summary?.customer?.stripe_id || 'Not linked'}</div>
							</div>
							<div>
								<div className="text-muted">Email</div>
								<div>{summary?.customer?.email || '-'}</div>
							</div>
							<div>
								<div className="text-muted">Card</div>
								<div>
									{paymentMethod
										? `${this.titleCase(paymentMethod.brand) || 'Card'} ****${paymentMethod.last4}`
										: 'No card on file'}
								</div>
							</div>
						</div>
					</CardContent>
				</Card>

				<Card className="mb-3">
					<CardContent>
						<h6>Subscriptions</h6>
						<ReactTable
							data={subscriptions}
							columns={subscriptionColumns}
							defaultPageSize={10}
							className="-striped -highlight"
							showPagination={true}
						/>
					</CardContent>
				</Card>

				<Card className="mb-3">
					<CardContent>
						<h6>Billing History</h6>
						<ReactTable
							data={billing}
							columns={billingColumns}
							defaultPageSize={10}
							className="-striped -highlight"
							showPagination={true}
						/>
					</CardContent>
				</Card>

				<Card className="mb-3">
					<CardContent>
						<h6>Activity</h6>
						<ReactTable
							data={activity}
							columns={activityColumns}
							defaultPageSize={10}
							className="-striped -highlight"
							showPagination={true}
							noDataText="No AV Center activity yet"
						/>
					</CardContent>
				</Card>

				<Framework.Elements.LoadingSpinner isLoading={loading}/>

				{canManage ? (
					<Framework.Elements.UniversalModal
						key={this.props.parent}
						hasSubmit={false}
						regions={this.props.regions}
						title={"Make a Sale"}
						name={"Make a Sale"}
						object={Objects.StripeCharge}
						history={this.props.history}
						toggle={this.state.modalStripeAdd}
						showModal={this.state.modalStripeAdd}
						reportClose={() => this.setState({modalStripeAdd: false})}
						size={"xl"}
						customer={this.props.parent}
						objectProps={{
							isNew: true,
							customer: this.props.parent,
							avCenter: true,
							afterSave: async () => {
								this.setState({modalStripeAdd: false});
								await this.loadSummary();
							},
						}}
					/>
				) : null}
			</div>
		);
	}
}
